import {
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  ICON_SIZES,
  IconName,
  InlineFormattedNumber,
  PopperHeader,
  Text,
  useBlotterTableContext,
  useCurrency,
  useSecurity,
  VStack,
  type ISubAccountCollateralStatus16016PriceResults,
} from '@talos/kyoko';
import styled, { useTheme } from 'styled-components';
import {
  formatCollateralHealthRatio,
  formatCollateralPriceLabel,
  formatShortfallPctOfMta,
  getCollateralContractualSource,
  getCollateralHealthMarkerColor,
  getCollateralHealthMarkerPlacement,
  getCollateralHealthMarkerRole,
  getCollateralHealthStatusDisplayName,
  getCollateralHealthThresholds,
  getCollateralRatioTextColor,
  getPrimaryCollateralShortfallView,
  getShortfallAccentColor,
  getStatusColor,
  hasDefinedMinTransferAmount,
  hasPrimaryCollateralShortfall,
} from './collateralStatusUtils';
import type {
  CounterpartyMonitoringCollateralizedDealWithStatusDetails,
  CounterpartyMonitoringRow,
} from './counterpartyMonitoringRows';

const TooltipContent = styled(Flex)`
  min-width: 380px;
  max-width: 480px;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.borderColorPopover};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  overflow: hidden;
`;

const TooltipBody = styled(VStack)`
  padding: ${({ theme }) => theme.spacing12}px;
`;

const PriceResultsGrid = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content auto;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing16}px;
  row-gap: ${({ theme }) => theme.spacing2}px;
`;

const PriceLabelCell = styled(Flex)`
  min-width: 0;
  align-items: center;
  justify-content: flex-start;
`;

const StatusCell = styled(HStack)`
  justify-content: flex-start;
`;

const ShortfallSection = styled(VStack)`
  align-items: stretch;
`;

const AccentText = styled(Text)<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

const StatusDot = styled(Icon)<{ $dimmed?: boolean }>`
  opacity: ${({ $dimmed }) => ($dimmed ? 0.5 : 1)};
`;

const ThresholdGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: ${({ theme }) => theme.spacing12}px ${({ theme }) => theme.spacing4}px;
  background: ${({ theme }) => `linear-gradient(to bottom, ${theme.colors.gray['020']}, ${theme.backgroundContent})`};
`;

const Threshold = styled(VStack)`
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    width: 1px;
    height: 16px;
    transform: translateY(-50%);
    background: ${({ theme }) => theme.colors.gray['040']};
  }
`;

function display(value: string | null | undefined) {
  return value == null || value === '' ? '-' : value;
}

function PriceResultCells({
  priceResult,
  row,
  index,
}: {
  priceResult: ISubAccountCollateralStatus16016PriceResults;
  row: CounterpartyMonitoringRow;
  index: number;
}) {
  const theme = useTheme();
  const role = getCollateralHealthMarkerRole(index);
  const isPrimary = role === 'above';
  const placement = getCollateralHealthMarkerPlacement(row, priceResult.Ratio);
  const zoneIndex = placement?.zoneIndex;
  const markerColor = getCollateralHealthMarkerColor({
    isPrimary,
    status: priceResult.HealthStatus,
    zoneIndex,
    theme,
  });
  const statusDisplayName = getCollateralHealthStatusDisplayName(priceResult.HealthStatus);
  const statusColor = getStatusColor(priceResult.HealthStatus, theme);
  const textColor = isPrimary ? 'colorTextImportant' : 'gray.090';
  const ratioColor = getCollateralRatioTextColor({
    isPrimary,
    status: priceResult.HealthStatus,
    theme,
  });
  const arrowIcon = role === 'above' ? IconName.TriangleDown : IconName.TriangleUp;
  const contractualSource = isPrimary ? getCollateralContractualSource(row) : undefined;

  return (
    <>
      <PriceLabelCell gap="spacing4">
        <Icon
          icon={arrowIcon}
          size={ICON_SIZES.MEDIUM}
          color={markerColor}
          data-testid={`collateral-status-source-marker-${role}`}
          data-part-id={`source-marker-${role}`}
          aria-label={statusDisplayName || priceResult.HealthStatus}
        />
        <Text
          color={textColor}
          fontSize="fontSizeSm"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          data-testid="collateral-status-price-label"
        >
          {formatCollateralPriceLabel(priceResult.PriceLabel)}
          {contractualSource != null && (
            <Text as="span" color="gray.090" data-testid="collateral-status-contractual-source">
              {` (${contractualSource})`}
            </Text>
          )}
        </Text>
      </PriceLabelCell>
      <StatusCell gap="spacing4" data-testid="collateral-status-status-cell">
        <StatusDot
          icon={IconName.DotSolid}
          size={ICON_SIZES.MEDIUM}
          color={statusColor}
          $dimmed={!isPrimary}
          data-testid="collateral-status-status-dot"
        />
        <Text color={textColor} fontSize="fontSizeSm" whiteSpace="nowrap">
          {display(statusDisplayName)}
        </Text>
      </StatusCell>
      <Text
        color={ratioColor}
        fontSize="fontSizeSm"
        whiteSpace="nowrap"
        textAlign="right"
        data-testid="collateral-status-ratio"
      >
        {display(formatCollateralHealthRatio(priceResult.Ratio, row))}
      </Text>
    </>
  );
}

function ThresholdDetail({ label, value }: { label: string; value: string | undefined }) {
  return (
    <Threshold gap="spacing4" alignItems="center">
      <Text color="colorTextSubtle" whiteSpace="nowrap" fontSize="fontSizeSm">
        {label}
      </Text>
      <Text color="colorTextImportant" whiteSpace="nowrap" fontSize="fontSizeSm">
        {display(value)}
      </Text>
    </Threshold>
  );
}

function ShortfallAmountSection({ row }: { row: CounterpartyMonitoringCollateralizedDealWithStatusDetails }) {
  const theme = useTheme();
  const shortfallView = getPrimaryCollateralShortfallView(row);
  const mtaCurrency = shortfallView?.minTransferAmountCurrency ?? shortfallView?.shortfallCurrency;
  const collateralCurrencyInfo = useCurrency(shortfallView?.collateralAsset);
  const shortfallCurrencyInfo = useCurrency(shortfallView?.shortfallCurrency);
  const mtaCurrencyInfo = useCurrency(mtaCurrency);

  if (!shortfallView) {
    return null;
  }

  const hasMta = hasDefinedMinTransferAmount(shortfallView.minTransferAmount);
  const accentColor = getShortfallAccentColor({
    shortfallPctMTA: hasMta ? shortfallView.shortfallPctMTA : undefined,
    status: shortfallView.status,
    theme,
  });
  const pctOfMta = hasMta ? formatShortfallPctOfMta(shortfallView.shortfallPctMTA) : undefined;

  return (
    <ShortfallSection gap="spacing4" data-testid="collateral-status-shortfall-section">
      <HStack w="100%" justifyContent="space-between" gap="spacing16" alignItems="flex-start">
        <Text color="colorTextImportant" fontSize="fontSizeSm">
          Shortfall Amount
        </Text>
        <AccentText
          $color={accentColor}
          fontSize="fontSizeSm"
          whiteSpace="nowrap"
          textAlign="right"
          data-testid="collateral-status-shortfall-collateral"
        >
          {shortfallView.shortfallCollateral != null && shortfallView.collateralAsset != null ? (
            <InlineFormattedNumber
              number={shortfallView.shortfallCollateral}
              currency={shortfallView.collateralAsset}
              increment={collateralCurrencyInfo?.DefaultIncrement}
              align="right"
              highlightColor={accentColor}
            />
          ) : (
            display(undefined)
          )}
        </AccentText>
      </HStack>
      {hasMta && (
        <HStack w="100%" justifyContent="space-between" gap="spacing16" alignItems="flex-start">
          <Text fontSize="fontSizeSm" whiteSpace="nowrap" data-testid="collateral-status-shortfall-pct-mta">
            <AccentText as="span" $color={accentColor}>
              {display(pctOfMta)}
            </AccentText>
            <Text as="span" color="colorTextSubtle">
              {' of MTA'}
            </Text>
          </Text>
          <Text
            fontSize="fontSizeSm"
            whiteSpace="nowrap"
            textAlign="right"
            data-testid="collateral-status-shortfall-of-mta"
          >
            <AccentText as="span" $color={accentColor}>
              <InlineFormattedNumber
                number={shortfallView.shortfall}
                currency={null}
                increment={shortfallCurrencyInfo?.DefaultIncrement}
                align="right"
                highlightColor={accentColor}
              />
            </AccentText>
            <Text as="span" color="colorTextSubtle">
              {` of `}
              <InlineFormattedNumber
                number={shortfallView.minTransferAmount}
                currency={mtaCurrency}
                increment={mtaCurrencyInfo?.DefaultIncrement}
                align="right"
              />
            </Text>
          </Text>
        </HStack>
      )}
    </ShortfallSection>
  );
}

export function CollateralStatusTooltip({
  row,
  close,
}: {
  row: CounterpartyMonitoringCollateralizedDealWithStatusDetails;
  close: () => void;
}) {
  const { counterpartiesByName, marketAccountDisplayNameByName } = useBlotterTableContext();
  const security = useSecurity(row.Symbol);
  const status = row.collateralStatusDetails;
  const counterparty = counterpartiesByName?.get(row.Counterparty ?? '');
  const counterpartyDisplayName = counterparty?.DisplayName ?? counterparty?.Name ?? row.Counterparty;
  const marketAccountDisplayName = marketAccountDisplayNameByName?.get(row.MarketAccount ?? '') ?? row.MarketAccount;
  const showShortfallSection = hasPrimaryCollateralShortfall(row);
  const thresholds = getCollateralHealthThresholds(row);

  return (
    <TooltipContent data-testid="collateral-status-tooltip">
      <PopperHeader title="Collateral Status" onCloseClicked={close} boxProps={{ pl: 'spacing12' }} />

      <TooltipBody gap="spacing12" alignItems="stretch">
        <VStack gap="spacing4" alignItems="stretch">
          <HStack w="100%" justifyContent="space-between" gap="spacing16">
            <Text color="colorTextImportant" fontSize="fontSizeSm">
              {display(security?.DisplaySymbol || row.Symbol)}
            </Text>
            <Text color="colorTextSubtle" fontSize="fontSizeSm" whiteSpace="nowrap">
              {row.productType}
            </Text>
          </HStack>
          <HStack w="100%" justifyContent="space-between" gap="spacing16">
            <Text color="colorTextSubtle" fontSize="fontSizeSm">
              {display(counterpartyDisplayName)}
            </Text>
            <Text color="colorTextSubtle" fontSize="fontSizeSm" textAlign="right">
              {display(marketAccountDisplayName)}
            </Text>
          </HStack>
        </VStack>

        <PriceResultsGrid data-testid="collateral-status-price-results">
          {status.PriceResults?.map((priceResult, index) => (
            <PriceResultCells
              key={`${priceResult.PriceLabel || 'price-result'}-${index}`}
              priceResult={priceResult}
              row={row}
              index={index}
            />
          ))}
        </PriceResultsGrid>

        {showShortfallSection && (
          <>
            <Divider orientation="horizontal" />
            <ShortfallAmountSection row={row} />
          </>
        )}
      </TooltipBody>

      {thresholds.length > 0 && (
        <ThresholdGrid data-testid="collateral-status-thresholds">
          {thresholds.map(threshold => (
            <ThresholdDetail
              key={threshold.label}
              label={threshold.label}
              value={formatCollateralHealthRatio(threshold.value, row)}
            />
          ))}
        </ThresholdGrid>
      )}
    </TooltipContent>
  );
}
